import React, { useEffect } from 'react';
import { Actions as USAGEACTIONS } from '../store/actions/usageActions';
import { Actions as ADMINACTIONS } from '../store/actions/adminActions';
import { Actions as CONN_ACTIONS } from '../store/actions/connectionActions';
import { connect } from 'react-redux';
import UsageDataTable from '../components/UsageDataNew/UsageDataTable';
import Breadcrumb from '../components/Common/Breadcrumb';
import { NoPermissions } from '../components/Common/ReusableComponents/Reusable';
import { Card, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import UploadUsageData from '../components/UsageDataNew/UploadUsageData';
import ImportResultModal from '../components/UsageDataNew/ImportResultModal';
import UploadHistory from '../components/UsageDataNew/UploadHistory';
import AddEditUsage from '../components/UsageData/AddEditUsage';
import DeleteUsageDataModal from '../components/UsageData/DeleteUsageDataModal';
import PendingImportsPanel from '../components/Connections/PendingImportsPanel';
import { Loader2, RefreshCw, Calendar, Bookmark, BarChart3, CloudUpload, BookOpen, MapPin } from 'lucide-react';

const Usage = ({
  organisation,
  getUsageTotals,
  processing,
  branding,
  industriesList,
  currencies,
  getAllItemsFromDB,
  hasAccess,
  permissions,
  facilities,
  usageDataGuideUrl,
  addUsageIssue,
  orgMissingCompulsoryData,
  currentOrganisation,
  showUsageDataModal,
  showUploadUsageDataModal,
  getMyUploads,
  showUploadHistory,
  usageUploadModal,
  usageUploadHistory,
  addUsageData,
  usageDataDeleteModal,
  importSummary,
  getImportSummary,
  importModal,
}) => {
  const { headers } = branding?.fonts?.[0]?.font;
  const usageIssue = addUsageIssue || orgMissingCompulsoryData;

  useEffect(() => {
    if (!currencies) {
      getAllItemsFromDB('currencies');
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentOrganisation) {
      getUsageTotals();
      getImportSummary();
    }
    getAllItemsFromDB('usageTypeDetails');
    getAllItemsFromDB('electricitySources');
    //eslint-disable-next-line
  }, [currentOrganisation]);

  if (permissions != undefined && !hasAccess) return <NoPermissions />;

  return (
    <React.Fragment>
      <Breadcrumb items={[{ label: 'Usage', link: '/usage' }]} />

      <Card className='border shadow-none bg-white'>
        <CardHeader className='flex flex-row items-center justify-between p-4'>
          <div className='flex flex-col gap-0 w-full'>
            <CardTitle className='flex flex-row items-center justify-between w-full uppercase text-lg font-semibold tracking-wide text-header-foreground mb-2 font-default'>
              <span className='truncate'>{organisation?.name}</span>
              <Button
                variant='ghost'
                size='icon'
                className='ml-2 text-header-foreground hover:text-primary hover:bg-transparent transition-colors'
                onClick={getUsageTotals}
                aria-label='Refresh usage data'
                disabled={processing}>
                {processing ? <Loader2 size={20} strokeWidth={1.5} className="animate-spin" /> : <RefreshCw size={20} strokeWidth={1.5} />}
              </Button>
            </CardTitle>
            <div className='flex flex-col gap-0'>
              <div className='flex flex-row items-center py-2 border-b border-muted last:border-b-0'>
                <Calendar size={20} strokeWidth={1.5} className='mr-3 text-header-foreground' />
                <span className='font-medium text-header-foreground mr-2'>Reporting year end</span>
                <span className='ml-auto text-header-foreground font-normal'>
                  {organisation?.reportingYearEndDay}/{organisation?.reportingYearEndMonth}
                </span>
              </div>
              <div className='flex flex-row items-center py-2'>
                <Bookmark size={20} strokeWidth={1.5} className='mr-3 text-header-foreground' />
                <span className='font-medium text-header-foreground mr-2'>Industry sector</span>
                <span className='ml-auto text-header-foreground font-normal'>
                  {industriesList?.find((i) => i.id === organisation?.industry)?.name}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {facilities?.length > 0 && !usageIssue && (
        <div className='my-6'>
          <PendingImportsPanel importSummary={importSummary} />
        </div>
      )}

      <div className='flex flex-wrap gap-4 my-6'>
        {facilities?.length > 0 && !usageIssue && (
          <Button id='add-usage-button' onClick={() => showUsageDataModal(true, {})}>
            <BarChart3 size={16} strokeWidth={1.5} /> Add usage data
          </Button>
        )}
        {facilities?.length > 0 && !usageIssue && (
          <Button
            id='UsageData-importUsageButton'
            onClick={() => showUploadUsageDataModal(true)}>
            <CloudUpload size={16} strokeWidth={1.5} /> Import usage data
          </Button>
        )}
        {facilities?.length > 0 && !usageIssue && (
          <Button asChild>
            <a target='_blank' href={usageDataGuideUrl}>
              <BookOpen size={16} strokeWidth={1.5} /> Usage Data Guide
            </a>
          </Button>
        )}
        {facilities?.length > 0 && !usageIssue && (
          <Button
            onClick={() => {
              getMyUploads();
              showUploadHistory(true);
            }}>
            <CloudUpload size={16} strokeWidth={1.5} /> Freight Import History
          </Button>
        )}
        {usageIssue && (
          <Button
            onClick={() => window.location.assign(`/organisations/${currentOrganisation}`)}>
            <MapPin size={16} strokeWidth={1.5} />{' '}
            {addUsageIssue
              ? `${addUsageIssue} before adding usage data`
              : 'Please complete your organisation details before adding usage data'}
          </Button>
        )}
        {facilities && facilities.length === 0 && (
          <Button onClick={() => window.location.assign('/facilities')}>
            <MapPin size={16} strokeWidth={1.5} /> Add a facility before adding usage data
          </Button>
        )}
      </div>

      <UsageDataTable />
      {addUsageData && <AddEditUsage />}
      {usageDataDeleteModal && <DeleteUsageDataModal />}
      {usageUploadModal && !importModal && <UploadUsageData />}
      {importModal && <ImportResultModal />}
      {usageUploadHistory > 0 && <UploadHistory />}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const { usageDataGuide } = state.whiteLabelAdmin;
  const orgDetails = state?.organisation?.details;
  const { orgMissingCompulsoryData } = state?.organisation;
  const addUsageIssue = (() => {
    // checkReportingYearEndAndIndustry logic inline
    if (!orgDetails?.reportingYearEndDay || !orgDetails?.reportingYearEndMonth) return 'Set reporting year end';
    if (!orgDetails?.industry) return 'Set industry sector';
    return null;
  })();
  return {
    organisation: state.organisation?.details,
    industriesList: state.organisation?.industries,
    processing: state.usageProcessing,
    branding: state.branding,
    currencies: state.admin?.adminPortal?.currencies,
    hasAccess: !!state.organisation?.permissions?.usageData?.view,
    permissions: state.organisation?.permissions,
    facilities: state?.facility?.list,
    usageDataGuideUrl: usageDataGuide,
    addUsageIssue,
    orgMissingCompulsoryData,
    currentOrganisation: state.currentOrganisation,
    addUsageData: state?.usageDataModal?.status,
    usageDataDeleteModal: state.usageDataDeleteModal?.status,
    usageUploadModal: state.usageUploadModal,
    usageUploadHistory: state.usageUploadHistory,
    importSummary: state.connections?.importSummary,
    importModal: state.importModal,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getUsageTotals: () => dispatch(USAGEACTIONS.getUsageTotals()),
  getAllItemsFromDB: (table) => dispatch(ADMINACTIONS.getAllItemsFromDB(table)),
  showUsageDataModal: (status, item) => dispatch(USAGEACTIONS.showUsageDataModal(status, item)),
  showUploadUsageDataModal: (status) => dispatch(USAGEACTIONS.showUploadUsageDataModal(status)),
  getMyUploads: () => dispatch(USAGEACTIONS.getMyUploads()),
  showUploadHistory: (status) => dispatch(USAGEACTIONS.showUploadHistory(status)),
  getImportSummary: () => dispatch(CONN_ACTIONS.getImportSummary()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
